<template>
  <b-overlay
    :show="loading"
    rounded="sm"
    variant="transparent"
    opacity="1"
    blur="1px"
    class="custom-overlay"
  >
    <b-container v-if="payout">
      <b-row class="account-info">
        <b-col cols="12" class="pb-3">
          <label class="m-0 mb-4">
            <h4 class="m-0 font-weight-bold">Account Information</h4>
          </label>

          <div>
            <b-row>
              <b-col cols="3" lg="1">
                <small class="grey-lighter">Name:</small>
              </b-col>
              <b-col cols="9" class="overflow-hidden text-monospace">
                <small>
                  {{ payout.name }}
                </small>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="3" lg="1">
                <small class="grey-lighter">Email:</small>
              </b-col>
              <b-col cols="9" class="overflow-hidden text-monospace">
                <small>
                  {{ payout.email }}
                </small>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="3" lg="1">
                <small class="grey-lighter">Country:</small>
              </b-col>
              <b-col cols="9">
                <div v-if="showCountries">
                  <b-form-select
                    v-model="newCountry"
                    :options="payout.allowedCountries"
                    class="form-control w-10 h-50 border-dark mr-2 ml-n1 my-2"
                    value-field="item"
                    text-field="name"
                  />
                  <b-button
                    class="mr-2 float-right"
                    size="sm"
                    variant="secondary"
                    @click="showCountries = false"
                  >
                    Cancel
                  </b-button>
                  <b-button
                    class="mr-2 float-right"
                    size="sm"
                    variant="primary"
                    @click="updateCountry()"
                  >
                    Select
                  </b-button>
                </div>
                <div v-else class="text-monospace text-uppercase">
                  <small v-if="payout.country" class="pr-2">
                    {{ payout.country }}
                  </small>
                  <b-link @click="showCountryList()">
                    <small>
                      <small v-if="hasBank">
                        <b>(change?)</b>
                      </small>
                      <small v-else>
                        <b>(select country)</b>
                      </small>
                    </small>
                  </b-link>
                </div>
              </b-col>
            </b-row>

            <b-row v-if="!showCountries">
              <b-col cols="3" lg="1">
                <small class="grey-lighter">Currency:</small>
              </b-col>
              <b-col cols="9">
                <small v-if="payout" class="text-monospace text-uppercase">
                  {{ payout.currency }}
                </small>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="3" lg="1">
                <small class="grey-lighter">Payouts:</small>
              </b-col>
              <b-col cols="9">
                <span class="text-monospace">
                  <span v-if="payout.status === `enabled`">
                    <b-badge variant="success">Enabled</b-badge>
                  </span>
                  <span v-else>
                    <b-badge variant="danger">Inactive</b-badge>
                  </span>
                </span>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="3" lg="1">
                <small class="grey-lighter">Schedule:</small>
              </b-col>
              <b-col cols="9">
                <span v-if="errorMessage" class="text-monospace">
                  <small class="text-warning">{{ errorMessage }}</small>
                </span>
                <span v-else class="text-monospace">
                  <small v-if="payout.status === `enabled`">
                    {{ payout.schedule }}
                  </small>
                  <small v-else class="text-warning">
                    <small>{{ payout.schedule }}</small>
                  </small>
                </span>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>

      <b-row class="bank-info" v-if="showBankDetails">
        <b-col cols="12">
          <label class="m-0 mb-4">
            <h4 class="m-0 font-weight-bold">Bank Details</h4>
          </label>

          <!-- Manual: Bank Details -->
          <bank-details
            v-if="hasBank && isPayoutManual"
            :bank_name="payout.bank.bank_name"
            :bank_address="payout.bank.bank_address"
            :residential_address="payout.bank.residential_address"
            :account_number="payout.bank.account_number"
            :routing_number="payout.bank.routing_number"
            :full_name="payout.bank.full_name"
          />

          <!-- Automatic: Stripe Bank Details -->
          <div
            v-else
            v-for="(bank, key) in payout.account.external_accounts.data"
            :key="key"
            class="mb-3"
          >
            <bank-details
              :bank_name="bank.bank_name"
              :account_number="`******` + bank.last4"
              :routing_number="bank.routing_number"
              :full_name="bank.account_holder_name"
            />
          </div>
        </b-col>
      </b-row>

      <!-- Button: Connect Stripe -->
      <b-row>
        <b-col cols="12" md="6" lg="4" v-if="canConnect">
          <b-button
            class="radius-30 w-100"
            variant="primary"
            @click="connectStripe"
          >
            Connect <b>Stripe</b> to update details
          </b-button>
          <small
            class="text-warning pl-2"
            v-if="!isPayoutManual && !showBankDetails"
          >
            Before connecting, make sure your country is correct.
          </small>
        </b-col>
      </b-row>

      <!-- MODAL FORMS -->
      <payout-option-modal
        :selected="payoutSelected"
        @continue="continuePayoutSelected"
      />
      <disclaimer-modal @continue="loadAccount(newCountry)" />
      <add-bank-modal :bank="payout.bank" @reload="loadAccount()" />
      <!-- MODAL FORMS END -->
    </b-container>
  </b-overlay>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import PayoutOptionModal from '../../components/PayoutOptionModal'
import DisclaimerModal from '../../components/DisclaimerModal'
import AddBankModal from '../../components/AddBankModal'
import BankDetails from '../../components/BankDetails'

export default {
  name: 'PayoutsPage',
  components: {
    PayoutOptionModal,
    DisclaimerModal,
    AddBankModal,
    BankDetails,
  },
  data() {
    return {
      loading: false,
      newCountry: null,
      showCountries: false,
    }
  },

  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      payout: (state) => state.auth.payout,
      errorMessage: (state) => state.auth.errorMessage,
    }),

    hasDueRequirements() {
      if (this.payout.account) {
        if (this.payout.account.requirements.currently_due.length > 0) {
          return true
        } else if (this.payout.account.requirements.eventually_due.length > 0) {
          return true
        } else if (this.payout.account.requirements.past_due.length > 0) {
          return true
        }
      }
      return false
    },

    canConnect() {
      return !(this.errorMessage || this.showCountries || this.isPayoutManual)
    },

    payoutSelected() {
      return this.payout ? this.payout.type : 'automatic'
    },

    isPayoutManual() {
      return this.payout && this.payout.type === 'manual'
    },

    hasBank() {
      if (this.isPayoutManual && this.payout.bank) {
        return true
      } else if (this.payout.account) {
        return true
      }
      return false
    },

    showBankDetails() {
      if (this.hasBank) {
        if (this.isPayoutManual) {
          return true
        } else if (this.payout.account.external_accounts.total_count > 0) {
          return true
        }
      }
      return false
    },
  },

  methods: {
    ...mapActions({
      getUserPayout: 'auth/getUserPayout',
      getCountries: 'auth/getCountries',
    }),

    connectStripe() {
      if (this.payout.accountLink) {
        this.loading = true
        window.location = this.payout.accountLink.url
      }
    },

    showCountryList() {
      this.showCountries = true

      if (this.isPayoutManual && this.payout.bank) {
        this.newCountry = this.payout.bank.country
      } else if (this.payout.account) {
        this.newCountry = this.payout.account.country
      } else {
        this.newCountry = 'AU'
      }
    },

    async loadAccount(country = null) {
      this.$bvModal.hide('disclaimerModal')

      this.loading = true
      await this.getUserPayout(country)

      this.loading = false
    },

    updateCountry() {
      this.showCountries = false
      this.$bvModal.show('disclaimerModal')
    },

    continuePayoutSelected(value) {
      this.$bvModal.hide('payoutOptionModal')

      if (value === 'automatic') {
        this.loadAccount(this.newCountry)
      } else {
        this.$bvModal.show('addBankModal')
      }
    },
  },

  created() {
    this.loadAccount()
  },
}
</script>

<style lang="scss"></style>
